import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from '@/hooks/ui/use-toast';
import type { BreathingExerciseBase as BreathingExercise } from '@/types/breathing';
import { QUERY_KEYS } from '@/config/queries';

export function useBreathing() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const completeSessionMutation = useMutation({
    mutationFn: async (exercise: BreathingExercise) => {
      if (!user) throw new Error('Not authenticated');

      const durationSeconds =
        (exercise.inhaleSeconds +
          exercise.holdSeconds +
          exercise.exhaleSeconds +
          exercise.holdAfterExhaleSeconds) *
        exercise.cycles;

      const pointsEarned = Math.floor(durationSeconds / 60) * 10; // 10 points per minute

      // Store breathing session (optional - can extend health_metrics or create new table)
      // For now, we'll just award points
      const { error: pointsError } = await supabase.from('points_ledger').insert({
        user_id: user.id,
        points: pointsEarned,
        transaction_type: 'earn',
        reason: `Breathing exercise: ${exercise.name}`,
      });

      if (pointsError) throw pointsError;

      return { pointsEarned, durationSeconds };
    },
    onSuccess: (data) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POINTS(user.id) });
      }
      toast({
        title: 'Breathing Exercise Complete! 🧘',
        description: `You earned ${data.pointsEarned} points for ${Math.round(data.durationSeconds / 60)} minutes of mindfulness.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    completeSession: completeSessionMutation.mutate,
    isCompleting: completeSessionMutation.isPending,
  };
}
